import "./index.css";
import React from "react";
import { Composition } from "remotion";
import { FORMAT } from "./brand";
import { HookReel, type HookReelProps } from "./templates/HookReel";
import {
  AppStorePreview,
  type AppStorePreviewProps,
} from "./templates/AppStorePreview";
import { OnboardingShowcase } from "./templates/OnboardingShowcase";
import { LaunchTeaser, type LaunchTeaserProps } from "./templates/LaunchTeaser";
import {
  FeatureShowcase,
  type FeatureShowcaseProps,
} from "./templates/FeatureShowcase";
import { BeforeAfter, type BeforeAfterProps } from "./templates/BeforeAfter";
import {
  WorkoutCountdown,
  type WorkoutCountdownProps,
} from "./templates/WorkoutCountdown";
import { KineticType, type KineticTypeProps } from "./templates/KineticType";
import { SnapToTimer, type SnapToTimerProps } from "./templates/SnapToTimer";
import { PasteToTimer, type PasteToTimerProps } from "./templates/PasteToTimer";
import { StatsReveal, type StatsRevealProps } from "./templates/StatsReveal";
import { AdThirtyDemo, type AdThirtyDemoProps } from "./templates/AdThirtyDemo";
import { PhotoParsingScene } from "./templates/3d/PhotoParsingScene";

const fps = 30;

const sampleHook: HookReelProps = {
  hook: "Stop fumbling with timers",
  punchline: "Just paste your workout",
  payoff: "AI does the rest.",
};

const sampleLaunchTeaser: LaunchTeaserProps = {
  badge: "JUST LAUNCHED",
  headline: "Your workout.\nYour timer.\nIn seconds.",
  cta: "Get the app",
};

const sampleFeatureShowcase: FeatureShowcaseProps = {
  appName: "AI WOD Timer",
  headline: "Any workout.\nInstant timer.",
};

const sampleAppStore: AppStorePreviewProps = {
  tagline: "AI WOD Timer",
  highlights: [
    {
      title: "Paste any workout",
      description: "Type, paste, or photograph. AI parses it.",
    },
    {
      title: "Every format",
      description: "AMRAP, EMOM, Tabata, For Time, intervals.",
    },
    {
      title: "Audio cues",
      description: "Beeps, callouts, splits — eyes off your phone.",
    },
  ],
  cta: "Get the app",
};

const sampleBeforeAfter: BeforeAfterProps = {
  hook: "Stop building\ntimers by hand.",
  beforeLabel: "The old way",
  afterLabel: "AI WOD Timer",
  manualSteps: [
    "Open timer app",
    "Pick AMRAP",
    "Set 20 minutes",
    "Type each movement",
    "Set the alert sound",
    "Save as preset",
    "Hit start. Finally.",
  ],
  cta: "Get the app →",
};

const sampleWorkoutCountdown: WorkoutCountdownProps = {
  eyebrow: "Tabata in 3 seconds",
  workoutType: "TABATA",
  workoutName: "8 Rounds",
  workSeconds: 20,
  restSeconds: 10,
  rounds: 8,
  movement: "Burpees",
};

const sampleKineticType: KineticTypeProps = {
  lines: [
    { text: "Stop", accent: "white" },
    { text: "building", accent: "white" },
    { text: "timers", accent: "white" },
    { text: "by hand.", accent: "action" },
  ],
  finalLine: "Paste.\nHit start.",
};

const sampleSnapToTimer: SnapToTimerProps = {
  eyebrow: "Snap your whiteboard",
  whiteboardText: "For time:\n21-15-9\nThrusters\nPull-ups",
  parsedTitle: "Fran",
  workoutType: "FOR TIME",
  totalRounds: 6,
  totalRests: 0,
  totalSecondsLabel: "08:00",
};

const samplePasteToTimer: PasteToTimerProps = {
  eyebrow: "Or just paste it",
  pastedWorkoutText: "For time:\n21-15-9\nThrusters\nPull-ups",
};

const sampleAdThirtyDemo: AdThirtyDemoProps = {
  eyebrow: "Tabata in 30 seconds",
  parsedWorkoutText: "Tabata:\n8 rounds\n20s work / 10s rest\nBurpees",
  parsedTitle: "Tabata 8",
  workoutType: "TABATA",
  totalRounds: 8,
  totalRests: 7,
  totalSecondsLabel: "04:00",
  cta: "Get the app",
};

const sampleStatsReveal: StatsRevealProps = {
  eyebrow: "How fast",
  statValue: 3.4,
  statSuffix: "s",
  statLabel: "From photo of a whiteboard\nto a ready timer.",
  comparison: { label: "vs building by hand", value: "6 min" },
  footer: "AI WOD Timer — paste any workout.",
};

export const RemotionRoot: React.FC = () => (
  <>
    <Composition
      id="HookReel"
      component={HookReel}
      durationInFrames={210}
      fps={fps}
      width={FORMAT.reel.width}
      height={FORMAT.reel.height}
      defaultProps={sampleHook}
    />
    <Composition
      id="AppStorePreview"
      component={AppStorePreview}
      durationInFrames={900}
      fps={fps}
      width={FORMAT.appStore.width}
      height={FORMAT.appStore.height}
      defaultProps={sampleAppStore}
    />
    <Composition
      id="OnboardingShowcase"
      component={OnboardingShowcase}
      durationInFrames={422}
      fps={fps}
      width={FORMAT.reel.width}
      height={FORMAT.reel.height}
    />
    <Composition
      id="LaunchTeaser"
      component={LaunchTeaser}
      durationInFrames={240}
      fps={fps}
      width={FORMAT.reel.width}
      height={FORMAT.reel.height}
      defaultProps={sampleLaunchTeaser}
    />
    <Composition
      id="FeatureShowcase"
      component={FeatureShowcase}
      durationInFrames={300}
      fps={fps}
      width={FORMAT.reel.width}
      height={FORMAT.reel.height}
      defaultProps={sampleFeatureShowcase}
    />
    <Composition
      id="BeforeAfter"
      component={BeforeAfter}
      durationInFrames={300}
      fps={fps}
      width={FORMAT.reel.width}
      height={FORMAT.reel.height}
      defaultProps={sampleBeforeAfter}
    />
    <Composition
      id="WorkoutCountdown"
      component={WorkoutCountdown}
      durationInFrames={420}
      fps={fps}
      width={FORMAT.reel.width}
      height={FORMAT.reel.height}
      defaultProps={sampleWorkoutCountdown}
    />
    <Composition
      id="KineticType"
      component={KineticType}
      durationInFrames={sampleKineticType.lines.length * 36 + 90}
      fps={fps}
      width={FORMAT.reel.width}
      height={FORMAT.reel.height}
      defaultProps={sampleKineticType}
    />
    <Composition
      id="SnapToTimer"
      component={SnapToTimer}
      durationInFrames={360}
      fps={fps}
      width={FORMAT.reel.width}
      height={FORMAT.reel.height}
      defaultProps={sampleSnapToTimer}
    />
    <Composition
      id="PasteToTimer"
      component={PasteToTimer}
      durationInFrames={300}
      fps={fps}
      width={FORMAT.reel.width}
      height={FORMAT.reel.height}
      defaultProps={samplePasteToTimer}
    />
    <Composition
      id="StatsReveal"
      component={StatsReveal}
      durationInFrames={240}
      fps={fps}
      width={FORMAT.reel.width}
      height={FORMAT.reel.height}
      defaultProps={sampleStatsReveal}
    />
    <Composition
      id="AdThirtyDemo"
      component={AdThirtyDemo}
      durationInFrames={900}
      fps={fps}
      width={FORMAT.reel.width}
      height={FORMAT.reel.height}
      defaultProps={sampleAdThirtyDemo}
    />
    <Composition
      id="PhotoParsing3D"
      component={PhotoParsingScene}
      durationInFrames={150}
      fps={fps}
      width={FORMAT.landscapeHD.width}
      height={FORMAT.landscapeHD.height}
      defaultProps={{
        phoneColor: "#98969b",
        baseScale: 1,
      }}
    />
  </>
);
